import React, {useCallback, useEffect} from 'react'
import {Col, DangerAlert, H6, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {Form} from '@modifi/plugin-forms'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {useTranslation} from '@modifi/plugin-i18n'
import {generatePath} from '@modifi/router'
import {IntermediaryShareholderFormData} from '../constants'
import {ROUTE_COMPANY_PROFILE_INTERMEDIARY_SHAREHOLDERS} from '../../../../routes/constants'
import {ConnectedFormFooter} from '../../../../plugins/onboarding/components/form-cta-footer'
import IntermediaryShareHoldersFormContent from '../components/intermediary-shareholders-form-content'
import {EntityFormData} from '../../../../types/common'
import {CompanyUnlockRefresher} from '../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchIntermediaryShareholders} from '../../../../data/intermediary-shareholders'

export type ExistingIntermediaryShareHolderProps = {
  isLoading: boolean
  updatePending: boolean
  externalIntermediaryShareholderId: string
  formData: EntityFormData<IntermediaryShareholderFormData>
  onSubmit: (changes: Record<string, unknown>, externalIntermediaryShareholderId: string) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  redirect: (route: string) => void
  isValidShareholderId: boolean
  pageIsVerified: boolean
  canUpdateCompany: boolean
  shareholdingCompanyName: string
}

const ExistingIntermediaryShareHolder: React.FC<ExistingIntermediaryShareHolderProps> = ({
  updatePending,
  externalIntermediaryShareholderId,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  formData,
  onDiscard,
  redirect,
  isValidShareholderId,
  pageIsVerified,
  canUpdateCompany,
  shareholdingCompanyName,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (!isValidShareholderId) {
      redirect(generatePath(ROUTE_COMPANY_PROFILE_INTERMEDIARY_SHAREHOLDERS))
    }
  }, [isValidShareholderId, redirect])

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      onSubmit(changes, externalIntermediaryShareholderId)
    },
    [onSubmit, externalIntermediaryShareholderId]
  )

  if (isLoading) {
    return null
  }
  return (
    <CustomerUploadManager>
      {pageIsVerified && (
        <SuccessAlert>{t('onboarding-section.fully-verified-alert')}</SuccessAlert>
      )}
      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
      <Row>
        <Col>
          <ButtonLink
            data-target="intermediary-shareholder-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_INTERMEDIARY_SHAREHOLDERS)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H6>{shareholdingCompanyName}</H6>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        defaultValues={formData.defaultValues}
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <IntermediaryShareHoldersFormContent canUpdateCompany={canUpdateCompany} />

        <CompanyUnlockRefresher
          refetch={(): any[] => [aFetchIntermediaryShareholders()]}
          initialValues={formData.initialValues}
          defaultValues={formData.defaultValues}
        />
        <ConnectedFormFooter loading={updatePending} hasError={hasError} />
      </Form>
    </CustomerUploadManager>
  )
}

export default ExistingIntermediaryShareHolder
